/**
 * CalendarInviteCard — a parsed `.ics` meeting invite rendered as event details
 * (title, when, where, organizer, join link) instead of raw iCalendar text.
 *
 * Two placements share it: `inline` sits at the top of the reading pane (the
 * Thunderbird-style invitation banner, with its own Add-to-calendar action) and
 * `dialog` fills the attachment preview. The inline card omits the description
 * — the message body directly below already shows the same text.
 */

import { CalendarClock, MapPin, Repeat, User, Video } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { CalendarInviteEventDto, CalendarInvitePreview } from '@/lib/api';
import { findMeetingJoinLink, formatInviteWhen, isCalendarInvite } from '../lib/calendar-invite';
import { useEmailCalendarInvitePreview } from '../hooks/useMailbox';
import { AddToCalendarButton } from './AddToCalendarButton';

interface Props {
  invite: CalendarInvitePreview;
  variant: 'inline' | 'dialog';
  /** Inline only: renders the Add-to-calendar split button (no id = invite calendar). */
  onAddToCalendar?: (calendarId?: string) => void;
  addingToCalendar?: boolean;
}

function InviteEvent({
  event,
  variant,
}: {
  event: CalendarInviteEventDto;
  variant: Props['variant'];
}) {
  const join = findMeetingJoinLink(event);
  const organizer = event.organizer;
  return (
    <div className="min-w-0 space-y-1">
      <div className="flex flex-wrap items-center gap-x-2">
        <span className="font-medium leading-snug">{event.title || '(untitled event)'}</span>
        {event.status === 'cancelled' && (
          <span className="text-xs font-medium text-destructive">Cancelled</span>
        )}
        {event.status === 'tentative' && (
          <span className="text-xs font-medium text-muted-foreground">Tentative</span>
        )}
      </div>
      <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
        <CalendarClock className="h-3.5 w-3.5 shrink-0" aria-hidden />
        <span>{formatInviteWhen(event.startAt, event.endAt, event.isAllDay)}</span>
        {event.isRecurring && (
          <span className="inline-flex items-center gap-1 text-xs">
            <Repeat className="h-3 w-3" aria-hidden /> Recurring
          </span>
        )}
      </div>
      {event.location && (
        <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
          <MapPin className="h-3.5 w-3.5 shrink-0" aria-hidden />
          <span className="truncate">{event.location}</span>
        </div>
      )}
      {organizer && (
        <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
          <User className="h-3.5 w-3.5 shrink-0" aria-hidden />
          <span className="truncate">
            Organizer: {organizer.name ? `${organizer.name} <${organizer.email}>` : organizer.email}
          </span>
        </div>
      )}
      {join && (
        <div className="pt-1">
          <Button asChild variant="outline" size="sm" className="h-7 gap-1">
            <a href={join.url} target="_blank" rel="noopener noreferrer" title={join.url}>
              <Video className="h-3.5 w-3.5" aria-hidden /> Join {join.service} meeting
            </a>
          </Button>
        </div>
      )}
      {variant === 'dialog' && event.description && (
        <p className="line-clamp-6 whitespace-pre-wrap pt-1 text-sm text-muted-foreground">
          {event.description}
        </p>
      )}
    </div>
  );
}

export function CalendarInviteCard({ invite, variant, onAddToCalendar, addingToCalendar }: Props) {
  if (invite.events.length === 0) {
    return (
      <div className="text-center text-sm text-muted-foreground">
        <CalendarClock className="mx-auto mb-2 h-6 w-6" />
        <p>This invite has no event details to show.</p>
        {invite.errors.length > 0 && (
          <p className="mt-1 text-xs text-destructive">{invite.errors[0]}</p>
        )}
      </div>
    );
  }

  if (variant === 'inline') {
    // An Outlook series update carries the master plus overrides sharing one
    // UID; the first VEVENT is the meeting itself — the rest are exceptions.
    const [primary] = invite.events;
    const more = invite.events.length - 1;
    return (
      <section
        aria-label="Meeting invitation"
        className="mb-4 flex flex-col gap-3 rounded-lg border bg-card p-3 sm:flex-row sm:items-start sm:justify-between"
      >
        <div className="min-w-0 space-y-1">
          <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Meeting invitation
          </div>
          <InviteEvent event={primary} variant="inline" />
          {more > 0 && (
            <p className="text-xs text-muted-foreground">
              + {more} more {more === 1 ? 'event' : 'events'} in this invite
            </p>
          )}
        </div>
        {onAddToCalendar && (
          <AddToCalendarButton onAdd={onAddToCalendar} disabled={addingToCalendar} />
        )}
      </section>
    );
  }

  return (
    <div className="w-full max-w-xl space-y-3 self-start">
      {invite.events.map((ev, i) => (
        <div key={i} className="rounded-lg border bg-card p-4 text-left">
          <InviteEvent event={ev} variant="dialog" />
        </div>
      ))}
      {invite.warnings.length > 0 && (
        <p className="text-xs text-muted-foreground">{invite.warnings[0]}</p>
      )}
    </div>
  );
}

/**
 * The inline invite card for an open message: picks the message's first
 * calendar attachment, fetches its parsed preview, and renders the `inline`
 * card. Renders nothing while loading, on error, or when the invite has no
 * parseable events — the attachment row below still offers preview/download,
 * and that is where a parse error is surfaced.
 */
export function MessageCalendarInvite({
  messageId,
  attachments,
  onAddToCalendar,
  addingToCalendar,
}: {
  messageId: string;
  attachments: ReadonlyArray<{ attachmentId: string; filename: string; mimeType: string }>;
  onAddToCalendar: (attachmentId: string, calendarId?: string) => void;
  addingToCalendar?: boolean;
}) {
  const inviteAttachment = attachments.find((a) => isCalendarInvite(a.mimeType, a.filename));
  const { data: invite } = useEmailCalendarInvitePreview(messageId, inviteAttachment?.attachmentId);
  if (!inviteAttachment || !invite || invite.events.length === 0) return null;
  return (
    <CalendarInviteCard
      invite={invite}
      variant="inline"
      onAddToCalendar={(calendarId) => onAddToCalendar(inviteAttachment.attachmentId, calendarId)}
      addingToCalendar={addingToCalendar}
    />
  );
}
